/** @module csi/card/_base */
define([
	"underscore",
	"csi/core/ExtendedPath",
	"csi/def",
	"csi/core/Key",
	"csi/reflection",
	"csi/core/SpecBuilder",
	"csi/xml",
	"csi/xml/Query"
], function(_, ExtendedPath, def, Key, reflection, SpecBuilder, xml, Query) {

	/**
	 * @typedef {Object} AttributeCardSpecs
	 * Attribute and image specs for AttributeCard
	 * @property {Spec[]} attributeSpecs
	 * @property {Spec} imageSpec
	 */

	const NAME_KEY = new Key({prop: xml.PROP_NAME});
	const _mapSpecs = {};

	/**
	 * Return Key for image of given type if it contains special path
	 * @param {string} type
	 * @return {Key || null}
	 */
	function getAttributeCardImageKey(type) {
		let rc = reflection.getClass(type);
		if (!rc) {
			return null;
		}

		const defType = def.typeDefinition(type);
		const image = defType.image;
		let key = null;

		if (image) {
			// in the case of defining the "Image" tag
			const imageId = image.m_id;
			const imagePath = image.m_path;
			const imageIndex = image.m_index;

			// get the destination class if specified
			if (imagePath) {
				rc = rc.getPathClass(imagePath, true);
			}

			var ra = reflection.getClassAttribute(rc, imageId);
			console.assert(!!ra, "The image attribute '" + imageId + "' could not be found for the type '" + type + "'");

			key = new Key({
				id: imageId,
				index: imageIndex,
				path: imagePath
			});
		} else {
			// the normal case
			const path = rc.getImagePath(type);
			if (path !== null) {
				key = new Key({
					id: reflection.ATTR_IMAGES,
					index: reflection.DEFAULT_IMAGE_LABEL,
					path
				});
			}
		}

		return key;
	}

	/**
	 * Query for nodes needed to render card for node in current context
	 * @param {Context} ctx
	 * @return {Deferred|undefined}
	 */
	function getAttributeCardDependencies(ctx) {
		return getDependenciesForSpecs(ctx, getAttributeCardSpecs(ctx.getNodeType()));
	}

	/**
	 * Query for nodes needed to render card for node in current context
	 * @param {Context} ctx
	 * @param {CardSpecs} specs
	 * @return {Deferred}
	 */
	function getDependenciesForSpecs(ctx, specs) {
		const node = ctx.getNode();
		return ctx.cache.getNodeDependencies(node.$URL, getPathsForSpecs(specs));
	}

	/**
	 * Return paths that need to be cached to render card of given type
	 * @param {CardSpecs} specs
	 * @param {string} [pathPrefix] - prefix to add to any paths needed
	 * @param {string[]} [paths] - if not provided, array will be created and returned
	 * @param {boolean} [attributesOnly] - if true then only get paths for attribute specs
	 * @return {string[]}
	 */
	function getPathsForSpecs(specs, pathPrefix, paths, attributesOnly) {
		paths = paths || [];

		function add(spec) {
			if (!spec) {
				return;
			}

			let headingPaths = def.getHeadingDependencyPaths(spec.heading);

			if (pathPrefix) {
				headingPaths = headingPaths.map((path) => xml.path(pathPrefix, path));
			}

			const path = spec.getQueryPath();
			if (path) {
				headingPaths.push(path);
			}

			paths.splice(0, 0, ...headingPaths);
		}

		if (!attributesOnly) {
			add(specs.imageSpec);
		}

		specs.attributeSpecs.forEach(add);

		return paths;
	}

	/**
	 * Return paths that need to be cached to render card of given type
	 * @param {string} type
	 * @param {string} [pathPrefix] - prefix to add to any paths needed
	 * @param {string[]} [paths] - if not provided, array will be created and returned
	 * @return {string[]}
	 */
	function getAttributeCardPaths(type, pathPrefix, paths) {
		const cardSpecs = getAttributeCardSpecs(type);

		return getPathsForSpecs(cardSpecs, pathPrefix, paths);
	}

	/**
	 * Return attribute and image specs to use for attribute card of given domain type
	 * @param {string} type - domain type 
	 * @param {Key[]} [keys] - optional keys that can override configured keys
	 * @return {AttributeCardSpecs}
	 */
	function getAttributeCardSpecs(type, keys) {
		const bCache = !keys;
		var result = bCache ? _mapSpecs[type] : null;

		// if already cached, then return
		if (result) {
			return result;
		}

		const specs = [];
		const specBuilder = new SpecBuilder(type);

		// create attribute specs
		keys = keys || getAttributeCardKeys(type);

		if (keys.length) {
			// if keys are configured, then just convert to Spec
			_.each(keys, function(key) {
				specs.push(specBuilder.getSpecForKey(key));
			});
		} else {
			// if still no keys, then just add name property
			specs.push(specBuilder.getSpecForKey(NAME_KEY));
		}

		// create image spec
		const imageKey = getAttributeCardImageKey(type);
		const imageSpec = imageKey ? specBuilder.getSpecForKey(imageKey) : null;

		result = {
			attributeSpecs: _.compact(specs),
			imageSpec: imageSpec
		};

		if (bCache) {
			_mapSpecs[type] = result;
		}

		return result;
	}

	/**
	 * Get the first five attribute card keys
	 * @param {string} type 
	 * @param {boolean} [omitName]
	 * @return {Key[]}
	 */
	function getAttributeCardDetailedKeys(type, omitName) {
		let keys = getAttributeCardKeys(type);

		if (omitName) {
			const nameKey = NAME_KEY.toString();
			keys = keys.filter((key) => nameKey !== key.toString());
		}

		return [...keys].splice(0, 5);
	}

	/**
	 * Get the first five attributes, omitting name if desired
	 * @param {string} type 
	 * @param {boolean} [omitName]
	 * @return {Object}
	 */
	function getAttributeCardDetailedSpecs(type, omitName) {
		const {attributeSpecs, imageSpec} = getAttributeCardSpecs(type);
		let detailedSpecs;

		if (omitName) {
			const nameKey = NAME_KEY.toString();
			detailedSpecs = attributeSpecs.filter((spec) => nameKey !== spec.key.toString());
		} else {
			detailedSpecs = [...attributeSpecs];
		}

		return {
			attributeSpecs: detailedSpecs.splice(0, 5),
			imageSpec,
		};
	}

	/**
	 * Return configured attribute keys for domain class of given type
	 * @param {string} type 
	 * @return {Key[]}
	 */
	function getAttributeCardKeys(type) {
		const dc = reflection.getDomainClass(type);
		const cardKeys = dc?.cardKeys || [];

		// it's possible that there are errors in the keys, so filter out untyped keys
		const keys = _.compact(cardKeys.map((dpkey) => {
			const key = new Key({dpkey});
			return key.isTyped ? key : null;
		}));

		if (keys.length) {
			return keys;
		}

		return [new Key({
			extendedPath: ExtendedPath.create(type),
			prop: xml.PROP_NAME,
		})];
	}

	/**
	 * Return configured attribute keys for view
	 * @param {C8NodeType} type 
	 * @param {boolean} showImage
	 * @return {Key[]}
	 */
	function getAttributeCardKeysForView(type, showImage) {
		const {isLocalizedItem} = reflection.getClass(type);
		let keys = [];

		getAttributeCardKeys(type).forEach((key) => {
			const {prop} = key;

			if (prop === xml.PROP_NAME) {
				keys.push(key.copy({
					id: xml.ATTR_NAME,
					prop: null,
				}));

				if (isLocalizedItem) {
					keys.push(key.copy({
						id: xml.ATTR_LOCALIZED_NAME,
						prop: null,
					}));
				}
			} else {
				// attributes in attribute card should not be hidden or disabled - it's possible attributes are
				// added to the card and later hidden or disabled
				const {reflectionAttribute} = key;

				if (!reflectionAttribute || (reflectionAttribute.isEnabled && !reflectionAttribute.isHidden)) {
					keys.push(key);
				}
			}
		});

		const imageKey = getAttributeCardImageKey(type);
		if (showImage && imageKey) {
			keys.push(imageKey);
		}

		return keys;
	}

	/**
	 * Get only the first two attributes
	 * @param {string} type 
	 * @return {Key[]}
	 */
	function getAttributeCardSlimKeys(type) {
		const keys = getAttributeCardKeys(type);
		return [...keys].splice(0, 2);
	}

	/**
	 * Get only the first two attributes
	 * @param {string} type 
	 * @return {Object}
	 */
	function getAttributeCardSlimSpecs(type) {
		const {attributeSpecs, imageSpec} = getAttributeCardSpecs(type);

		return {
			attributeSpecs: [...attributeSpecs].splice(0, 2),
			imageSpec
		};
	}

	/**
	 * Return attribute specs without the spec for the node name
	 * @param {Spec[]} specs 
	 * @return {Spec[]}
	 */
	function getAttributeSpecsMinusName(attributeSpecs) {
		return attributeSpecs.filter((spec) => spec.queryKey !== xml.ATTR_NAME);
	}

	/**
	 * Generate query across given attribute specs for given search text
	 * @param {CardSpecs} specs 
	 * @param {string} searchText 
	 * @param {Query} [query] 
	 * @return {Query}
	 */
	function getQueryForSpecs(specs, searchText, query) {
		const {OP_MATCHES} = xml;
		const {attributeSpecs} = specs;
		const {length} = attributeSpecs;

		query = query || new Query();
		const q = (length > 1) ? query.Or() : query;

		for (const spec of attributeSpecs) {
			if (spec.canFilterByOperator(OP_MATCHES)) {
				spec.applyFilter(q, OP_MATCHES, searchText);
			}
		}

		return query;
	}

	return {
		getAttributeCardDependencies,
		getAttributeCardDetailedKeys,
		getAttributeCardDetailedSpecs,
		getAttributeCardImageKey,
		getAttributeCardKeys,
		getAttributeCardKeysForView,
		getAttributeCardPaths,
		getAttributeCardSlimKeys,
		getAttributeCardSlimSpecs,
		getAttributeCardSpecs,
		getAttributeSpecsMinusName,
		getDependenciesForSpecs,
		getPathsForSpecs,
		getQueryForSpecs,
	};
});